import {Fragment} from 'react'
import {Text} from '@primer/react-brand/esm'
import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import {BLOCKS, MARKS, type Document} from '@contentful/rich-text-types'
import {
  codeMarkRenderer,
  codeMarkRendererHeadingPillar,
} from '@github-ui/swp-core/components/contentful/shared/RichTextFieldRenderer'

const renderWithoutParagraphsWithMarks = (document: Document, renderMark: typeof codeMarkRenderer) => {
  return documentToReactComponents(document, {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_, children) => <>{children}</>,
    },
    renderMark,
  })
}

const toId = (text: string) =>
  text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')

/*
 * Preserve line breaks by converting \n into <br /> elements.
 */
export const renderWithNewlines = (text: string) => {
  const lines = text.split('\n')
  return lines.map((line, index) => {
    const key = `${index}-${toId(line)}`
    return (
      <Fragment key={key}>
        {line}
        {index < lines.length - 1 && <br />}
      </Fragment>
    )
  })
}

/*
 * Renders rich text without paragraph wrappers.
 */
export const renderWithoutParagraphs = (heading: Document) => {
  return renderWithoutParagraphsWithMarks(heading, codeMarkRenderer)
}

export const renderPillarHeading = (heading: Document) =>
  renderWithoutParagraphsWithMarks(heading, codeMarkRendererHeadingPillar)

export const renderWithSpans = (heading: Document, className?: string) => {
  return documentToReactComponents(heading, {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_, children) => <span className={className}>{children}</span>,
    },
    renderMark: codeMarkRenderer,
  })
}

export const renderPoints = (heading: Document) => {
  return documentToReactComponents(heading, {
    renderMark: {
      ...codeMarkRenderer,
      [MARKS.BOLD]: text => <Text variant="default">{text}</Text>,
    },
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_, children) => (
        <Text as="p" variant="muted">
          {children}
        </Text>
      ),
    },
  })
}
